import React, { useState, useEffect } from 'react';
import { Copy, Check, Code, Layout } from 'lucide-react';
import axiosInstance from '@/services/axiosInstance';
import './styles.scss';

export default function PartnerIntegration() {
  const [institutions, setInstitutions] = useState([]);
  const [selectedInst, setSelectedInst] = useState('');
  const [copied, setCopied] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchInstitutions = async () => {
      try {
        const response = await axiosInstance.get('/api/advising/institutions');
        if (response.data && Array.isArray(response.data)) {
          setInstitutions(response.data);
          if (response.data.length > 0) {
            setSelectedInst(response.data[0].shortName || response.data[0]);
          }
        }
      } catch (error) {
        console.error('Failed to fetch institutions:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchInstitutions();
  }, []);

  const getSlug = (name) => {
    const slugMap = {
      'University of Memphis': 'uom',
      'Tennessee State University': 'tsu',
      'Middle Tennessee State University': 'mtsu'
    };
    return slugMap[name] || name.toLowerCase().replace(/[^a-z0-9]+/g, '-');
  };

  const getEmbedCode = (format = 'html') => {
    const baseUrl = 'https://smartadvising.ai';
    const slug = getSlug(selectedInst);
    
    if (format === 'react') {
      return `<iframe 
  src="${baseUrl}/embed/${slug}" 
  width="100%" 
  height="1050px" 
  style={{ border: 'none', display: 'block' }}
  title="PolyTechnicApp AI Advisor Embed"
></iframe>`;
    }

    return `<iframe 
  src="${baseUrl}/embed/${slug}" 
  width="100%" 
  height="1050px" 
  style="border: none; display: block;"
  title="PolyTechnicApp AI Advisor Embed"
></iframe>`;
  };

  const handleCopy = (format) => {
    navigator.clipboard.writeText(getEmbedCode(format));
    setCopied(format);
    setTimeout(() => setCopied(null), 2000);
  };

  if (loading) {
    return <div className="partner-integration-loading">Loading configuration...</div>;
  }

  return (
    <div className="partner-integration-page animate-fade-in">
      <div className="page-header">
        <div className="header-icon"><Layout size={24} /></div>
        <div className="header-text">
          <h1>Partner Integration</h1>
          <p>Generate secure iframe embed codes for partner institutions.</p>
        </div>
      </div>

      <div className="integration-card">
        <div className="card-section">
          <label className="section-label">1. Select Target Institution</label>
          <p className="section-help">Choose the institution namespace you want to generate an embed for.</p>
          <select
            className="institution-select"
            value={selectedInst}
            onChange={(e) => setSelectedInst(e.target.value)}
          >
            {institutions.map(inst => {
              const shortName = inst.shortName || inst;
              const fullName = inst.fullName || inst.shortName || inst;
              return (
                <option key={shortName} value={shortName}>
                  {fullName} ({shortName})
                </option>
              );
            })}
          </select>
        </div>

        <div className="card-section">
          <label className="section-label">2. Embed Code</label>
          <p className="section-help">Copy the appropriate snippet and paste it into your target website or application.</p>
          
          <div className="code-wrapper" style={{ marginBottom: '1.5rem' }}>
            <div className="code-header">
              <div className="code-title">
                <Code size={18} />
                <span>HTML Snippet</span>
              </div>
              <button className={`copy-btn ${copied === 'html' ? 'copied' : ''}`} onClick={() => handleCopy('html')}>
                {copied === 'html' ? <><Check size={16} /> Copied!</> : <><Copy size={16} /> Copy HTML</>}
              </button>
            </div>
            <pre className="code-block">
              <code>{getEmbedCode('html')}</code>
            </pre>
          </div>

          <div className="code-wrapper">
            <div className="code-header">
              <div className="code-title">
                <Code size={18} />
                <span>React (JSX / TSX) Snippet</span>
              </div>
              <button className={`copy-btn ${copied === 'react' ? 'copied' : ''}`} onClick={() => handleCopy('react')}>
                {copied === 'react' ? <><Check size={16} /> Copied!</> : <><Copy size={16} /> Copy JSX/TSX</>}
              </button>
            </div>
            <pre className="code-block">
              <code>{getEmbedCode('react')}</code>
            </pre>
          </div>
        </div>

        <div className="card-section preview-section">
          <label className="section-label">Live Preview URL</label>
          <div className="preview-url-box">
            <a href={`https://smartadvising.ai/embed/${getSlug(selectedInst)}`} target="_blank" rel="noreferrer">
              https://smartadvising.ai/embed/{getSlug(selectedInst)}
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
